import React, { useEffect } from 'react';
import { useState } from 'react';
import { User as UserIcon, Lock, Eye, EyeOff, CreditCard, TrendingUp, TrendingDown, BarChart2 } from 'lucide-react';
import { isAuthenticated } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const SignIn = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (isAuthenticated()) navigate('/home', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Simulate network delay
    setTimeout(() => {
      if (formData.email === 'admin' && formData.password === '123') {
        const mockUser = {
          id: 1,
          role: 'admin',
          email: 'admin@debtracker.com',
        };
        localStorage.setItem('token', 'mock-jwt-token-12345');
        localStorage.setItem('user', JSON.stringify(mockUser));
        localStorage.setItem('user_email', mockUser.email);
        localStorage.setItem('user_role', mockUser.role);
        navigate('/home', { replace: true });
      } else {
        setError('Invalid username or password');
      }
      setIsLoading(false);
    }, 600);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const features = [
    { icon: TrendingDown, color: 'text-emerald-400 bg-emerald-400/15', label: 'Track money owed to you', sub: 'Never forget a receivable again' },
    { icon: TrendingUp, color: 'text-rose-400 bg-rose-400/15', label: 'Manage your payables', sub: 'Stay on top of what you owe' },
    { icon: BarChart2, color: 'text-indigo-300 bg-indigo-400/15', label: 'Analytics & insights', sub: 'Visualize your debt landscape' },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)' }}
      >
        {/* Blobs */}
        <div className="absolute top-1/4 -left-24 w-72 h-72 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #818cf8, transparent 70%)' }} />
        <div className="absolute bottom-1/4 -right-16 w-56 h-56 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #a78bfa, transparent 70%)' }} />

        <div className="relative z-10 flex flex-col justify-center px-14 text-white">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/30 border border-indigo-400/30 flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">DebTracker</h1>
              <p className="text-indigo-300 text-xs">Smart Debt Management</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold mb-3 leading-tight">Track every<br />cent, effortlessly.</h2>
          <p className="text-indigo-200 text-base mb-10 leading-relaxed">
            Know exactly who owes you and what you owe — all in one beautiful, organized dashboard.
          </p>

          <div className="space-y-5">
            {features.map((f) => (
              <div key={f.label} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${f.color}`}>
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-indigo-200">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">DebTracker</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
            <p className="text-sm text-gray-500">Sign in to your DebTracker account</p>
          </div>

          {/* Glass form card */}
          <div className="rounded-2xl p-8"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {error && (
              <div className="mb-5 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email / Username */}
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-400 mb-1.5">Username or Email</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    id="email" name="email" type="text" required
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    placeholder="admin"
                    value={formData.email} onChange={handleChange}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    id="password" name="password" type={showPassword ? 'text' : 'password'} required
                    className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    placeholder="••••••••"
                    value={formData.password} onChange={handleChange}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember / Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-indigo-600 rounded" />
                  <span className="text-xs text-gray-500">Remember me</span>
                </label>
                <a href="#" className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors font-medium">
                  Forgot password?
                </a>
              </div>

              {/* Submit */}
              <button
                type="submit" disabled={isLoading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in…
                  </>
                ) : 'Sign In'}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-600 mt-6">
            Need help? <a href="#" className="text-indigo-400 hover:text-indigo-300">Contact support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;